import { getAnalyticsContext } from '../../analytics';
import {
    getCtaParametersForwardingAllowlist,
    getCtaParametersForwardingMode,
    getSessionUtmParameterName,
    getVisitorUtmParameterName,
} from '../../config/SiteConfigManager';
import { appendForwardedParams, getForwardedUrlParams } from './ctaParameterForwarding';

function appendParamsWithoutUrlParsing(url: string, params: URLSearchParams): string {
    if (!params.toString()) return url;

    const hashIndex = url.indexOf('#');
    const urlWithoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
    const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
    const queryIndex = urlWithoutHash.indexOf('?');
    const path = queryIndex === -1 ? urlWithoutHash : urlWithoutHash.slice(0, queryIndex);
    const query = queryIndex === -1 ? '' : urlWithoutHash.slice(queryIndex + 1);
    const searchParams = new URLSearchParams(query);

    params.forEach((value, key) => {
        searchParams.set(key, value);
    });

    const queryString = searchParams.toString();
    return queryString ? `${path}?${queryString}${hash}` : `${path}${hash}`;
}

export function appendCtaAttributionParams(baseUrl: string, domain: string): string {
    const { sessionId, clientId } = getAnalyticsContext();
    const sessionParamName = getSessionUtmParameterName(domain);
    const visitorParamName = getVisitorUtmParameterName(domain);
    const forwardingMode = getCtaParametersForwardingMode(domain);
    const forwardingAllowlist = getCtaParametersForwardingAllowlist(domain);
    const forwardedParams = getForwardedUrlParams(forwardingMode, forwardingAllowlist);
    const hasForwardedParams = Array.from(forwardedParams.keys()).length > 0;

    const hasAttribution = Boolean(
        (sessionId && sessionParamName) || (clientId && visitorParamName) || hasForwardedParams
    );
    if (!hasAttribution) {
        return baseUrl;
    }

    try {
        const url = new URL(baseUrl, window.location.href);

        if (sessionId && sessionParamName) {
            url.searchParams.set(sessionParamName, sessionId);
        }
        if (clientId && visitorParamName) {
            url.searchParams.set(visitorParamName, clientId);
        }
        appendForwardedParams(url.searchParams, forwardedParams);

        return url.toString();
    } catch {
        const params = new URLSearchParams();

        if (sessionId && sessionParamName) {
            params.set(sessionParamName, sessionId);
        }
        if (clientId && visitorParamName) {
            params.set(visitorParamName, clientId);
        }

        // For the fallback path we cannot parse the URL itself, so we only set
        // forwarded params here when they are absent from the literal
        // query string of `baseUrl`. This keeps the no-overwrite contract.
        if (hasForwardedParams) {
            const queryIndex = baseUrl.indexOf('?');
            const hashIndex = baseUrl.indexOf('#');
            const queryEnd = hashIndex === -1 ? baseUrl.length : hashIndex;
            const existingQuery = queryIndex === -1 ? '' : baseUrl.slice(queryIndex + 1, queryEnd);
            const existing = new URLSearchParams(existingQuery);
            forwardedParams.forEach((value, key) => {
                if (!existing.has(key) && !params.has(key)) {
                    params.set(key, value);
                }
            });
        }

        return appendParamsWithoutUrlParsing(baseUrl, params);
    }
}
